import React, { useEffect, useMemo, useState } from 'react';
import { Bell, Info } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';

const defaultsByStrategy = {
  SHORT_STRANGLE: { dte: 21, delta: 0.3 },
  IRON_CONDOR: { dte: 14, delta: 0.25 },
};

const getShortLegScopes = (operation) => {
  const legs = operation?.legs || [];
  const hasShortPut = legs.some((leg) => String(leg?.position_side).toUpperCase() === 'SHORT' && String(leg?.option_type).toUpperCase() === 'PUT');
  const hasShortCall = legs.some((leg) => String(leg?.position_side).toUpperCase() === 'SHORT' && String(leg?.option_type).toUpperCase() === 'CALL');
  const scopes = [];
  if (hasShortPut) scopes.push('short_put');
  if (hasShortCall) scopes.push('short_call');
  return scopes;
};

export const OptionsPositionAlertQuickDialog = ({ open, onOpenChange, operation, rules = [], preferences, onCreateRule, onUpdateRule, onToggleRule }) => {
  const { t, formatMessage } = useOptionsLocale();
  const [rows, setRows] = useState([]);

  const strategyDefaults = defaultsByStrategy[operation?.strategyLabel] || { dte: 21, delta: 0.3 };
  const operationRules = useMemo(() => rules.filter((item) => item.operation_id === operation?.id), [operation?.id, rules]);
  const shortLegScopes = useMemo(() => getShortLegScopes(operation), [operation]);
  const currencyLabel = operation?.underlying || '';

  useEffect(() => {
    if (!operation) return;
    const dteRule = operationRules.find((item) => item.alert_type === 'dte_roll_window');
    const profitRule = operationRules.find((item) => item.alert_type === 'profit_target');
    const maxLossRule = operationRules.find((item) => item.alert_type === 'max_loss');
    const strikeRule = operationRules.find((item) => item.alert_type === 'strike_proximity');
    const deltaRows = shortLegScopes.map((legScope) => {
      const rule = operationRules.find((item) => item.alert_type === 'leg_delta' && (item.condition?.leg_scope || 'any_short') === legScope);
      return {
        key: legScope === 'short_put' ? 'deltaPut' : 'deltaCall',
        type: 'leg_delta',
        legScope,
        label: t(`optionsAlerts.quickRows.${legScope === 'short_put' ? 'deltaPut' : 'deltaCall'}`),
        enabled: !!rule?.enabled,
        value: String(rule?.condition?.threshold_value ?? strategyDefaults.delta),
        rule,
        helperText: t('optionsAlerts.deltaHelperText'),
      };
    });
    setRows([
      { key: 'dte', type: 'dte_roll_window', label: t('optionsAlerts.quickRows.dte'), enabled: !!dteRule?.enabled, value: String(dteRule?.condition?.threshold_value ?? strategyDefaults.dte), rule: dteRule },
      ...deltaRows,
      { key: 'profit', type: 'profit_target', label: formatMessage(t('optionsAlerts.quickRows.profit'), { currency: currencyLabel }), enabled: !!profitRule?.enabled, value: String(profitRule?.condition?.threshold_value ?? '0.05'), rule: profitRule },
      { key: 'loss', type: 'max_loss', label: formatMessage(t('optionsAlerts.quickRows.maxLoss'), { currency: currencyLabel }), enabled: !!maxLossRule?.enabled, value: String(Math.abs(maxLossRule?.condition?.threshold_value ?? -0.05)), rule: maxLossRule },
      { key: 'strike', type: 'strike_proximity', label: t('optionsAlerts.quickRows.strike'), enabled: !!strikeRule?.enabled, value: String((Number(strikeRule?.condition?.threshold_value ?? 0.03) * 100).toFixed(1)), rule: strikeRule },
    ]);
  }, [operation, operationRules, shortLegScopes, strategyDefaults.delta, strategyDefaults.dte, currencyLabel, t, formatMessage]);

  const handleSave = async () => {
    let success = true;
    for (const row of rows) {
      const payload = buildPayloadFromRow(row, operation, preferences);
      if (row.rule) {
        if (row.enabled) {
          if (row.rule.enabled) {
            success = (await onUpdateRule(row.rule.id, payload)) && success;
          } else {
            success = (await onToggleRule(row.rule.id, true)) && success;
          }
        } else if (row.rule.enabled) {
          success = (await onToggleRule(row.rule.id, false)) && success;
        }
      } else if (row.enabled) {
        success = (await onCreateRule(payload)) && success;
      }
    }
    if (success) onOpenChange(false);
  };

  if (!operation) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-[#07111f] text-white sm:max-w-[640px]" data-testid="options-position-alert-quick-dialog">
        <DialogHeader>
          <DialogTitle data-testid="options-position-alert-quick-dialog-title">{operation.underlying} · {operation.strategyLabel}</DialogTitle>
          <DialogDescription className="text-white/60" data-testid="options-position-alert-quick-dialog-description">
            {t('optionsAlerts.quickDialogDescription')}
          </DialogDescription>
        </DialogHeader>

        <TooltipProvider>
          <div className="space-y-3">
            {rows.map((row) => (
              <div key={row.key} className="grid gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4 md:grid-cols-[minmax(0,1fr)_120px] md:items-center" data-testid={`options-position-alert-row-${row.key}`}>
                <div className="flex items-start gap-3">
                  <Checkbox checked={row.enabled} onCheckedChange={(checked) => setRows((current) => current.map((item) => item.key === row.key ? { ...item, enabled: checked } : item))} data-testid={`options-position-alert-row-check-${row.key}`} />
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-semibold text-white">{row.label}</p>
                    {row.helperText ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" className="text-white/30 transition-colors hover:text-white/60" data-testid={`options-position-alert-row-tooltip-trigger-${row.key}`} aria-label={row.label}>
                            <Info size={13} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[240px] border border-white/10 bg-[#11161E] text-white/90" data-testid={`options-position-alert-row-tooltip-content-${row.key}`}>
                          {row.helperText}
                        </TooltipContent>
                      </Tooltip>
                    ) : null}
                  </div>
                </div>
                <input value={row.value} onChange={(event) => setRows((current) => current.map((item) => item.key === row.key ? { ...item, value: event.target.value } : item))} className="h-10 rounded-xl border border-white/10 bg-black/20 px-3 text-sm text-white outline-none focus:border-[#F0B90B]" data-testid={`options-position-alert-row-input-${row.key}`} />
              </div>
            ))}
          </div>
        </TooltipProvider>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="ghost" className="text-white/70 hover:bg-white/10 hover:text-white" onClick={() => onOpenChange(false)} data-testid="options-position-alert-quick-dialog-cancel">
            {t('optionsAlerts.cancel')}
          </Button>
          <Button type="button" className="rounded-full bg-[#F0B90B] px-5 text-black hover:bg-[#ffd26a]" onClick={handleSave} data-testid="options-position-alert-quick-dialog-save">
            <Bell size={16} className="mr-2" />
            {t('optionsAlerts.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const buildPayloadFromRow = (row, operation, preferences) => {
  const common = {
    enabled: true,
    scope_type: 'operation',
    scope_id: operation.id,
    operation_id: operation.id,
    strategy_label: operation.strategyLabel,
    underlying: operation.underlying,
    channels: { in_app: true, email: !!preferences?.email_enabled },
    metadata: { locale: preferences?.locale || 'ptBR', timezone: preferences?.timezone || 'UTC' },
  };
  if (row.type === 'dte_roll_window') return { ...common, alert_type: row.type, condition: { threshold_value: Number(row.value || 21) } };
  if (row.type === 'leg_delta') return { ...common, alert_type: row.type, condition: { threshold_value: Number(row.value || 0.3), leg_scope: row.legScope || 'any_short' } };
  if (row.type === 'profit_target') return { ...common, alert_type: row.type, condition: { unit: 'native', comparator: 'gte', threshold_value: Number(row.value || 0.05) } };
  if (row.type === 'max_loss') return { ...common, alert_type: row.type, condition: { unit: 'native', comparator: 'lte', threshold_value: -Math.abs(Number(row.value || 0.05)) } };
  return { ...common, alert_type: row.type, condition: { target: 'any_short', threshold_value: Number(row.value || 3) / 100 } };
};

export default OptionsPositionAlertQuickDialog;